<template>
  <div class="completion-view">
    <div class="audit-container">
      <div class="view-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <i class="pi pi-check-circle"></i>
          </div>
          <div class="header-text">
            <h1 class="view-title">Audit Complete</h1>
            <p class="view-description">Review the audit results below</p>
          </div>
        </div>
      </div>

      <div class="main-content-layout">
        <div class="summary-column">
          <div class="summary-card">
            <div class="summary-stats">
              <div class="stat-item">
                <div class="stat-label">Expected</div>
                <div class="stat-value">{{ expectedAnimals.length }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Scanned</div>
                <div class="stat-value">{{ scannedCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Expected Scanned</div>
                <div class="stat-value">{{ expectedScannedCount }}</div>
              </div>
            </div>
          </div>

          <div v-if="!hasExceptions" class="success-card">
            <div class="success-icon">
              <i class="pi pi-check-circle"></i>
            </div>
            <h3 class="success-title">All Animals Accounted For</h3>
            <p class="success-message">
              All {{ expectedAnimals.length }} expected animals have been successfully scanned.
            </p>
          </div>
        </div>

        <div v-if="hasExceptions" class="exceptions-column">
          <ExceptionSummary
            :missing-animals="missingAnimals"
            :unexpected-animals="unexpectedAnimals"
            :cage-number-mismatch="cageNumberMismatch"
            :show-supervisor-prompt="false"
          />
        </div>
      </div>

      <div class="actions-section">
        <button class="btn btn-primary" @click="handleStartNewAudit">Start New Audit</button>
        <button
          v-if="hasExceptions"
          class="btn btn-secondary"
          @click="handleNotifySupervisor"
          :disabled="isNotifying"
        >
          {{ isNotifying ? 'Notifying...' : 'Notify Supervisor' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuditStore } from '../../stores/audit.store';
import { useAuthStore } from '../../stores/auth.store';
import {
  checkCompletionStatus,
  prepareExceptionData,
} from '../../services/audit-completion.service';
import { publishExceptionEvent } from '../../services/audit.service';
import { isUnauthorizedError } from '../../services/api-error.service';
import { redirectToLogin } from '../../services/auth-redirect.service';
import ExceptionSummary from '../../components/audit/ExceptionSummary.vue';

const router = useRouter();
const auditStore = useAuditStore();
const authStore = useAuthStore();

const expectedAnimals = computed(() => auditStore.expectedAnimals);
const scannedAnimals = computed(() => auditStore.scannedAnimals);
const cageInfo = computed(() => auditStore.cageInfo);
const isNotifying = ref(false);

const completionStatus = computed(() =>
  checkCompletionStatus(expectedAnimals.value, scannedAnimals.value),
);

const scannedCount = computed(() => auditStore.getScannedCount());
const expectedScannedCount = computed(() => auditStore.getExpectedScannedCount());
const hasExceptions = computed(() => completionStatus.value.hasExceptions);
const missingAnimals = computed(() => completionStatus.value.missingAnimals);
const unexpectedAnimals = computed(() => completionStatus.value.unexpectedAnimals);

// Check for cage number mismatch (this would be set during cage verification)
const cageNumberMismatch = computed(() => false); // TODO: Track this during cage verification

const findExpectedAnimalByScanToken = (token: string) => {
  const normalizedToken = token.trim().toUpperCase();

  return expectedAnimals.value.find(
    (animal) =>
      animal.rapidTagBarcode === normalizedToken ||
      animal.rfidTagId?.trim().toUpperCase() === normalizedToken,
  );
};

/**
 * Handle starting a new audit
 */
function handleStartNewAudit(): void {
  auditStore.reset();
  router.push({ name: 'audit-start' });
}

/**
 * Handle supervisor notification
 */
async function handleNotifySupervisor(): Promise<void> {
  const exceptionData = prepareExceptionData(
    expectedAnimals.value,
    scannedAnimals.value,
    cageNumberMismatch.value,
  );

  const correlationId = auditStore.currentLookup?.correlationId;
  const cageId = cageInfo.value?.id;

  if (!correlationId || !cageId) {
    alert('Missing audit information. Cannot notify supervisor.');
    return;
  }

  // Generate audit ID (for now, use correlationId as auditId)
  const auditId = correlationId;

  isNotifying.value = true;

  try {
    const token = authStore.accessToken;
    if (!token) {
      alert('Authentication required. Please log in again.');
      isNotifying.value = false;
      return;
    }

    await publishExceptionEvent(
      {
        correlationId,
        cageId,
        auditId,
        exceptions: {
          missingAnimals: exceptionData.missingAnimals.map((barcode) => {
            const animal = findExpectedAnimalByScanToken(barcode);
            return {
              id: animal?.id || '',
              rapidTagBarcode: barcode,
              color: animal?.color || animal?.rapidTagColour || '',
            };
          }),
          unexpectedAnimals: unexpectedAnimals.value.map((scannedAnimal) => {
            // For unexpected animals, we don't have color info from expected animals
            return {
              rapidTagBarcode: scannedAnimal.rapidTagBarcode,
              color: '', // Unexpected animals don't have color info from expected list
            };
          }),
          cageNumberMismatch: exceptionData.cageNumberMismatch,
        },
      },
      token,
    );

    alert('Supervisor notification sent successfully. Please wait for supervisor approval.');
  } catch (error) {
    if (isUnauthorizedError(error)) {
      authStore.clearAuth();
      await redirectToLogin(router, router.currentRoute.value.fullPath);

      return;
    }

    console.error('Failed to publish exception event', error);
    alert('Failed to notify supervisor. Please try again.');
  } finally {
    isNotifying.value = false;
  }
}

/**
 * Automatically publish exception event if unexpected animals are found
 */
async function publishExceptionIfNeeded(): Promise<void> {
  // Only publish if there are unexpected animals
  if (unexpectedAnimals.value.length === 0) {
    return;
  }

  const exceptionData = prepareExceptionData(
    expectedAnimals.value,
    scannedAnimals.value,
    cageNumberMismatch.value,
  );

  const correlationId = auditStore.currentLookup?.correlationId;
  const cageId = cageInfo.value?.id;

  if (!correlationId || !cageId) {
    console.warn('Missing audit information. Cannot publish exception event.');
    return;
  }

  // Generate audit ID (for now, use correlationId as auditId)
  const auditId = correlationId;

  try {
    const token = authStore.accessToken;
    if (!token) {
      console.warn('Authentication required. Cannot publish exception event.');
      return;
    }

    await publishExceptionEvent(
      {
        correlationId,
        cageId,
        auditId,
        exceptions: {
          missingAnimals: exceptionData.missingAnimals.map((barcode) => {
            const animal = findExpectedAnimalByScanToken(barcode);
            return {
              id: animal?.id || '',
              rapidTagBarcode: barcode,
              color: animal?.color || animal?.rapidTagColour || '',
            };
          }),
          unexpectedAnimals: unexpectedAnimals.value.map((scannedAnimal) => {
            // For unexpected animals, we don't have color info from expected animals
            return {
              rapidTagBarcode: scannedAnimal.rapidTagBarcode,
              color: '', // Unexpected animals don't have color info from expected list
            };
          }),
          cageNumberMismatch: exceptionData.cageNumberMismatch,
        },
      },
      token,
    );

    console.info('Exception event published automatically due to unexpected animals', {
      unexpectedCount: unexpectedAnimals.value.length,
      correlationId,
    });
  } catch (error) {
    if (isUnauthorizedError(error)) {
      authStore.clearAuth();
      await redirectToLogin(router, router.currentRoute.value.fullPath);

      return;
    }

    console.error('Failed to publish exception event automatically', error);
    // Don't show alert - user can still manually notify supervisor if needed
  }
}

/**
 * Check if we have audit data, if not redirect
 * Also automatically publish exception event if unexpected animals are found
 */
onMounted(async () => {
  if (!expectedAnimals.value || expectedAnimals.value.length === 0) {
    // No expected animals, redirect to start
    router.push({ name: 'audit-start' });
    return;
  }

  // Automatically publish exception event if there are unexpected animals
  await publishExceptionIfNeeded();
});
</script>

<style scoped>
.completion-view {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: 100dvh; /* Dynamic viewport height for mobile */
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-sizing: border-box;
}

.audit-container {
  background: white;
  border-radius: 1.5rem;
  padding: 2.5rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: auto;
}

.view-header {
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: left;
}

.header-text {
  flex: 1;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  flex-shrink: 0;
}

.view-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem;
  letter-spacing: -0.025em;
}

.view-description {
  color: #6b7280;
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.main-content-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.summary-card {
  background: #f9fafb;
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.summary-stats {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
}

.exceptions-column {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.success-card {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 0.75rem;
  padding: 1.25rem;
  text-align: center;
  color: white;
  flex-shrink: 0;
}

.success-icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
}

.success-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.375rem;
}

.success-message {
  font-size: 0.875rem;
  margin: 0;
  opacity: 0.95;
}

.actions-section {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-shrink: 0;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 0.875rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
}

/* Tablet/iPad specific styles */
@media (min-width: 768px) and (max-width: 1024px) {
  .completion-view {
    padding: 1rem;
  }

  .audit-container {
    padding: 2rem;
    gap: 1rem;
    max-width: 500px;
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
  }

  .view-title {
    font-size: 1.5rem;
  }

  .view-description {
    font-size: 0.875rem;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .success-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  .success-title {
    font-size: 1rem;
  }

  .success-message {
    font-size: 0.8125rem;
  }

  .btn {
    padding: 0.75rem 1.5rem;
    font-size: 0.9375rem;
  }
}

@media (max-width: 767px) {
  .completion-view {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .audit-container {
    padding: 1.5rem;
    max-width: 100%;
    border-radius: 1rem;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
  }

  .header-text {
    text-align: center;
  }

  .view-title {
    font-size: 1.5rem;
  }

  .view-description {
    font-size: 0.9rem;
  }

  .main-content-layout {
    flex-direction: column;
  }

  .summary-column {
    flex: none;
    width: 100%;
  }

  .stat-item {
    flex: 1;
    min-width: 0;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .actions-section {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    padding: 1rem;
  }
}
</style>
